import React from 'react';
import PropTypes from 'prop-types';
import './PhaseNavigator.css';

/**
 * Phase navigation component for progressing through development phases
 * @param {Object} props - Component props
 * @param {Array} props.phases - List of available phases
 * @param {string} props.currentPhase - Current active phase
 * @param {Function} props.onPhaseChange - Handler for phase change
 */
function PhaseNavigator({ phases, currentPhase, onPhaseChange }) {
  // Get current phase index
  const currentIndex = phases.indexOf(currentPhase);
  
  // Define phase display names and icons
  const phaseInfo = {
    'PLANNING': {
      name: 'Planning',
      icon: '📋',
      description: 'Define requirements and create component structure'
    },
    'DESIGN': {
      name: 'Design',
      icon: '🔍',
      description: 'Create technical specifications and file organization'
    },
    'CODING': {
      name: 'Coding',
      icon: '👨‍💻',
      description: 'Implement code in batches based on dependencies'
    },
    'REFINEMENT': {
      name: 'Refinement',
      icon: '🔧',
      description: 'Perform targeted modifications and improvements'
    },
    'FINISHING': {
      name: 'Finishing',
      icon: '✅',
      description: 'Finalize implementation with optimizations'
    }
  };
  
  // Handle phase click
  const handlePhaseClick = (phase, index) => {
    // Only allow navigating to previous phases or the next phase
    if (index <= currentIndex + 1) {
      onPhaseChange(phase);
    }
  };
  
  return (
    <div className="phase-navigator">
      <ul className="phase-list">
        {phases.map((phase, index) => {
          // Determine phase status
          const isActive = phase === currentPhase;
          const isCompleted = index < currentIndex;
          const isAvailable = index <= currentIndex + 1;
          
          // Apply appropriate CSS classes
          const classes = [
            'phase-item',
            isActive ? 'active' : '',
            isCompleted ? 'completed' : '',
            !isAvailable ? 'disabled' : ''
          ].filter(Boolean).join(' ');
          
          return (
            <li 
              key={phase} 
              className={classes}
              onClick={() => isAvailable && handlePhaseClick(phase, index)}
              title={phaseInfo[phase]?.description || phase}
            >
              <div className="phase-indicator">
                {isCompleted ? (
                  <span className="phase-check">✓</span>
                ) : (
                  <span className="phase-number">{index + 1}</span>
                )}
              </div>
              
              <div className="phase-content">
                <span className="phase-icon">{phaseInfo[phase]?.icon || '📄'}</span>
                <span className="phase-name">{phaseInfo[phase]?.name || phase}</span>
              </div>
              
              {index < phases.length - 1 && (
                <div className="phase-connector"></div>
              )}
            </li>
          );
        })}
      </ul>
      
      <div className="current-phase-info">
        <h2 className="current-phase-title">
          {phaseInfo[currentPhase]?.icon} {phaseInfo[currentPhase]?.name} Phase
        </h2>
        <p className="current-phase-description">
          {phaseInfo[currentPhase]?.description}
        </p>
      </div>
    </div>
  );
}

PhaseNavigator.propTypes = {
  phases: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentPhase: PropTypes.string.isRequired,
  onPhaseChange: PropTypes.func.isRequired
};

export default PhaseNavigator;
